import { ContentFooter } from "@app/components/core/ContentFooter/ContentFooter"
import { ContentHeader } from "@app/components/core/ContentHeader/ContentHeader"
import { MultilineText } from "@app/components/core/MultilineText/MultilineText"
import { SelectionGroup } from "@app/components/core/SelectionGroup/SelectionGroup"
import LoadingSelectorPage from "@app/components/loading/LoadingSelectorPage"
import Button from "@app/components/move2Library/Button/Button"
import { useCustomNavigation } from "@app/components/navigation/NavigationHandler"
import { LoungerTypeSelector } from "@app/components/order/LoungerTypeSelector/LoungerTypeSelector"
import OrderContentBody from "@app/components/order/OrderContentBody/OrderContentBody"
import OrderSubPage from "@app/components/pageTypes/OrderSubPage/OrderSubPage"
import { OrderAnimationTypes } from "@app/components/routing/AnimatedOrderRoute/AnimatedOrderRoute"
import LoungerGuaranteedModal from "@app/modals/funnel/LoungerGuaranteedModal/LoungerGuaranteedModal"
import { useRaiseError } from "@app/store/actions/errorActions"
import { addLounger, removeLounger, setObjectGroupOptions, setSelectedTimeblockKind } from "@app/store/actions/orderActions"
import { useGetLoungerTypes, useGetStrings } from "@app/store/service/cmsService"
import { useAvaiableObjects, useDeleteObjectOption, useSaveObjectGroupOptions, useTimeblocks } from "@app/store/service/connectorService"
import { appStore } from "@app/store/store"
import { handleKeyboardSelect } from "@app/utils/accessibility"
import { OrderErrors, TICKET_TYPES } from "@app/utils/constants"
import { useDelayLoading } from "@app/utils/hooks/useDelayLoading"
import { RenderHTML } from "@app/utils/htmlUtils"
import { getPriceString } from "@app/utils/localeUtils"
import {
	selectCountOfLoungerWithSelection,
	selectDateSelectionPrice,
	selectFullPrice,
	selectMinimumCapacity,
	selectMissingCapacity,
	selectNeededCapacity,
	selectUsedCapacity,
} from "@app/utils/selectors"
import tracking from "@app/utils/tracking"
import { faArrowRight } from "@fortawesome/free-solid-svg-icons/faArrowRight"
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useModal } from "@tecreation/funnel-components"
import { ResponseError, useSelector } from "@tecreation/js-state-react"
import { AnimatePresence, domAnimation, LazyMotion, m } from "framer-motion"
import { useCallback, useEffect, useState } from "react"
import Skeleton from "react-loading-skeleton"
import "./LoungerSelection.css"

export default function LoungerSelection() {
	const [validLoungers, setValidLoungers] = useState(undefined)
	const [inValidLoungers, setInValidLoungers] = useState(undefined)
	const loungers = useSelector(state => state.order.loungers)
	const neededCapacity = useSelector(selectNeededCapacity)
	const minimumCapacity = useSelector(selectMinimumCapacity)
	const usedCapacity = useSelector(selectUsedCapacity)
	const selectedDay = useSelector(state => state.order.selectedDay)
	const price = useSelector(selectFullPrice)
	const ticketPrice = useSelector(selectDateSelectionPrice)
	const missingCapacity = useSelector(selectMissingCapacity)
	const selectedTb = useSelector(state => state.order.selectedTb)
	const objectGroupOptions = useSelector(state => state.order.objectGroupOptions)
	const countOfLoungerWithSelection = useSelector(selectCountOfLoungerWithSelection)
	const { push } = useCustomNavigation()
	const {
		data: {
			noVacancy,
			loungerSelectionTitle,
			loungerSelectionIndividualLounger,
			ourRecommendation,
			buttonContinueWithoutLounger,
			buttonAutomaticAssign,
			loungerSelectionLoungerOnlyWithDayTicket,
			loungerSelectionSwitchToDayTicket,
			loungerSelectionSwitchToDayTicketInfo,
			onlyFew,
			moreInformation,
		},
	} = useGetStrings()
	const { data: loungerTypes } = useGetLoungerTypes({ locationId: appStore.getState().core.locationId, zoneId: appStore.getState().core.zoneId })
	const [{ isLoading: __firstLoadingTimeblocks, data: timeblockData }] = useTimeblocks()
	const [loadAvailableLoungers, { data: availableLoungers, isFetching: __loading }] = useAvaiableObjects()
	const [saveObjectGroupOptions, { isFetching: __isSavingObjectGroups }] = useSaveObjectGroupOptions()
	const [deleteReserverationForId] = useDeleteObjectOption()
	const raiseError = useRaiseError()
	const { showModal } = useModal()

	const firstLoadingTimeblocks = useDelayLoading(__firstLoadingTimeblocks, 50)
	const loading = useDelayLoading(__loading)
	const isSavingObjectGroups = useDelayLoading(__isSavingObjectGroups, 100)

	let isRestricted = selectedTb && selectedTb.items[0].restrictions && selectedTb && selectedTb.items[0].restrictions.objectRequired

	const relation = appStore.get("core.relation")
	const isHandicapped = relation && relation.tags ? (relation.tags.find(e => e.tag === "Handicap") ? true : false) : false

	useEffect(() => {
		;(async () => {
			try {
				loadAvailableLoungers({ date: selectedDay.date }).unwrap()
				//that is only neccesarry if we implement a lock screen if not a day ticket is selected
				// getTimeblocks({ locationId, zoneId, visitorKindIds, dateString: selectedDay.date }).unwrap();
			} catch (e) {
				raiseError(e, undefined, true)
			}

			Promise.all(
				objectGroupOptions.map(object => {
					return deleteReserverationForId({ objectOptionId: object.objectOptionId }).unwrap()
				}),
			).catch(e => {
				if (e instanceof ResponseError) {
					if (e && e.response.data && e.response.data.errorMessage && e.response.data.errorMessage.startsWith("wrn.Object.OptionNotFound")) {
						return
					}
				}
				raiseError(e, undefined, true)
			})
		})()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (loungerTypes && availableLoungers) {
			const validLoungers = {}
			const invalidLoungers = []
			for (let group in loungerTypes) {
				for (let lounger of loungerTypes[group]) {
					let availability = availableLoungers.find(e => e.objectGroupId === lounger.objectGroupId)
					if (availability) {
						if (availability.restCount > 0) {
							validLoungers[group] = validLoungers[group] || []
							validLoungers[group].push({
								...lounger,
								restCount: availability.restCount,
								price: availability.price,
								dynamicPriceLevelId: availability.dynamicPriceLevelId,
							})
						} else {
							invalidLoungers.push({
								...lounger,
								restCount: availability.restCount,
								price: availability.price,
								dynamicPriceLevelId: availability.dynamicPriceLevelId,
							})
						}
					} else {
						invalidLoungers.push({ ...lounger })
					}
				}
			}
			setValidLoungers(validLoungers)
			setInValidLoungers(invalidLoungers)
			appStore.set(state => ({
				order: {
					...state.order,
					availableLoungers: availableLoungers,
				},
			}))
		}
	}, [loungerTypes, availableLoungers])

	const next = useCallback(async () => {
		if (usedCapacity < minimumCapacity && usedCapacity > 0) {
			raiseError(new Error(OrderErrors.allAdultNeedsAnLounger))
			return
		}

		if (loungers.length) {
			try {
				let rsp = await saveObjectGroupOptions({ loungers: loungers, dateString: selectedDay.date }).unwrap()
				tracking.beds(true)
				setObjectGroupOptions([...rsp])
				push("/order/bluphoria-selection", OrderAnimationTypes.fullSlide)
			} catch (e) {
				return raiseError(e, undefined, false)
			}
		} else {
			push("/order/bluphoria-selection", OrderAnimationTypes.fullSlide)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loungers, countOfLoungerWithSelection])

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const openDetails = useCallback(lounger => {
		push("order/lounger-details", null, lounger)
	})
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const openLoungerMapSelection = useCallback(() => {
		push("order/loungermap-selection", null)
	})

	const showOnlyDayTicketOverlay = false
	//selectedTb.timeBlockTypeId !== TICKET_TYPES.WholeDay;
	const dayTicketTimeblock = timeblockData
		? timeblockData.find(e => e.timeBlocks.filter(a => a.timeBlockTypeId == TICKET_TYPES.WholeDay).length > 0)
		: undefined

	return (
		<OrderSubPage>
			<ContentHeader canGoBack colorStyle="white" animation={OrderAnimationTypes.fullSlide}>
				{loungerSelectionTitle}{" "}
				<span
					className="clickable ml-2 f3"
					role="button"
					tabIndex={0}
					aria-label={moreInformation}
					onClick={() => {
						showModal(LoungerGuaranteedModal)
					}}
					onKeyDown={e => {
						handleKeyboardSelect(e, () => showModal(LoungerGuaranteedModal))
					}}
				>
					<FontAwesomeIcon icon={faInfoCircle} aria-hidden="true" />
				</span>
			</ContentHeader>

			<OrderContentBody
				className="relative"
				style={{
					minHeight: "100%",
					background:
						"linear-gradient(#eaf1f5 30%, rgba(255, 255, 255, 0)), linear-gradient(rgba(255, 255, 255, 0), #eaf1f5 70%) 0 100%, linear-gradient(rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0)), linear-gradient(rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.05)) 0 100%",
				}}
				routeStyle={{
					overflow: showOnlyDayTicketOverlay ? "hidden" : "",
				}}
			>
				{showOnlyDayTicketOverlay && (
					<div className="absolute color-bg-secondary-100 top-0 left-0 h-100 w-100 z-9 flex flex-column py-6 px-8" style={{ opacity: ".97" }}>
						<div className="text-center f3">{loungerSelectionLoungerOnlyWithDayTicket}</div>
						<div className="flex-center flex-1 flex-column">
							<div className="color-fg-attention semibold">{loungerSelectionSwitchToDayTicket}</div>
							{firstLoadingTimeblocks ? (
								<div className="f00" style={{ width: "200px" }}>
									<Skeleton />
								</div>
							) : (
								<Button
									style="outlined"
									className="mt-2 primary"
									cssStyle={{ backgroundColor: "white" }}
									onClick={() => {
										if (dayTicketTimeblock) {
											setSelectedTimeblockKind(dayTicketTimeblock)
										}
									}}
								>
									{dayTicketTimeblock && (
										<span className="semibold">
											{dayTicketTimeblock.type} <span className="color-fg-secondary">+{getPriceString(dayTicketTimeblock.minPrice - ticketPrice)}</span>
										</span>
									)}
								</Button>
							)}
						</div>
						<div className="f5">
							<MultilineText>{loungerSelectionSwitchToDayTicketInfo}</MultilineText>
						</div>
					</div>
				)}

				<LoadingSelectorPage show={loading} showTitle length={5} />

				{validLoungers &&
					Object.keys(validLoungers).length > 0 &&
					Object.keys(validLoungers)
						.sort()
						.map((key, index) => {
							return (
								<SelectionGroup
									key={index}
									title={
										<>
											<span className="uppercase">{key}</span>
											{key.toLowerCase() === "comfort" && (
												<>
													<span> - </span>
													<span className="color-fg-success">{ourRecommendation}</span>
												</>
											)}
										</>
									}
								>
									{validLoungers[key].map(lounger => {
										let restCountIsUsed = lounger.restCount - loungers.filter(e => e.lounger_id === lounger.lounger_id).length <= 0
										let capacityIsReached = missingCapacity === 1 ? false : lounger.capacity + usedCapacity > neededCapacity
										return (
											<LoungerTypeSelector
												key={lounger.objectGroupId}
												onClick={() => openDetails(lounger)}
												count={loungers.filter(e => e.lounger_id === lounger.lounger_id).length}
												imgSrc={lounger.image}
												name={lounger.loungerType}
												persons={lounger.capacity}
												price={lounger.price}
												onAdd={() => addLounger(lounger)}
												onRemove={() => removeLounger(lounger)}
												expanded={
													lounger.description ? (
														<MultilineText className="px-2 py-4">
															<RenderHTML html={lounger.description} />
														</MultilineText>
													) : undefined
												}
												additionalInfo={lounger.restCount < 5 ? onlyFew : undefined}
												plusDisabled={restCountIsUsed || capacityIsReached}
											/>
										)
									})}
								</SelectionGroup>
							)
						})}

				{inValidLoungers && Object.keys(inValidLoungers).length > 0 && (
					<SelectionGroup
						title={
							<div>
								<span className="uppercase">{noVacancy}</span>
							</div>
						}
					>
						{inValidLoungers.map(lounger => {
							return (
								<LoungerTypeSelector
									key={lounger.objectGroupId}
									onClick={() => openDetails(lounger)}
									count={0}
									imgSrc={lounger.image}
									name={lounger.loungerType}
									persons={lounger.capacity}
									price={lounger.price}
									onAdd={() => addLounger(lounger)}
									onRemove={() => removeLounger(lounger)}
									disabled
								/>
							)
						})}
					</SelectionGroup>
				)}
				<LazyMotion features={domAnimation}>
					<AnimatePresence>
						{loungers.length > 0 ? (
							<m.button
								key="lounger-selection-bottom"
								className="lounger__bottom__btn of-hidden mt-4 color-bg-secondary-700 clickable rounded-3 boxShadow color-fg-white"
								initial={{
									height: 0,
								}}
								animate={{
									height: "auto",
									transition: {
										duration: 0.2,
										ease: "easeOut",
									},
								}}
								exit={{
									height: 0,
									transition: { duration: 0.2, ease: "easeOut" },
								}}
								onClick={openLoungerMapSelection}
							>
								<div className="flex py-4 mx-3 flex-items-center">
									<div className="f4 mr-3">
										<FontAwesomeIcon icon={faArrowRight} />
									</div>
									<div className="regular f4">{loungerSelectionIndividualLounger}</div>
								</div>
							</m.button>
						) : null}
					</AnimatePresence>
				</LazyMotion>
			</OrderContentBody>
			<ContentFooter
				process={3}
				price={price}
				priceAbsolute
				buttonProps={{
					text: isRestricted ? buttonAutomaticAssign : loungers.length ? buttonAutomaticAssign : buttonContinueWithoutLounger,
					isLoading: isSavingObjectGroups,
					disabled: (isHandicapped && loungers.length) || (isRestricted && !loungers.length),
					onNext: next,
					disabledReason: isHandicapped && loungers.length ? "Bitte wählen sie einen individuellen Liegeplatz" : undefined,
				}}
			/>
		</OrderSubPage>
	)
}
